import clsx from "clsx";
import { type JSX, type ReactNode, useEffect, useState } from "react";

import { useSession, type VoiceTask } from "@/sdk";

import { useIsPacingActive } from "../hooks/useIsPacingActive";
import { usePacingState } from "../hooks/usePacingState";
import classes from "./PacingBarOutline.module.css";

interface PacingBarOutlineProps {
    task: VoiceTask;
    children: ReactNode;
}

const clamp = (value: number, min = 0, max = 100): number => Math.min(Math.max(value, min), max);

/**
 * Renders a thin horizontal progress bar beneath the call controls bar that visualizes the
 * expert's pacing against the configured target. The track fades smoothly into view, the fill
 * grows from left to right as elapsed time approaches the target, and the fill color
 * transitions through the pacing states (onTrack → approaching → exceeded).
 *
 * Calls usePacingState with emitAnalytics=false because the CallDuration pill is the
 * primary subscriber for pacing threshold analytics; this wrapper only consumes the
 * derived visual state.
 */
export function PacingBarOutline({ task, children }: PacingBarOutlineProps): JSX.Element {
    const isPacingActive = useIsPacingActive();
    const { startedTimestamp, id: sessionId } = useSession();
    const callActive = task.status === "assigned";
    const pacing = usePacingState({
        partner: task.partner,
        sessionId,
        startedTimestamp,
        callActive,
        isPacingActive,
        emitAnalytics: false,
    });

    // Defer applying the visible class by one animation frame so the opacity transition
    // (and fill width transition) run even on the very first render where pacing already
    // has a target.
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (!pacing.hasTarget) {
            setIsVisible(false);
            return;
        }
        const frameId = requestAnimationFrame(() => setIsVisible(true));
        return () => cancelAnimationFrame(frameId);
    }, [pacing.hasTarget]);

    const fillWidth = isVisible ? clamp(pacing.progressPercent) : 0;

    return (
        <div className={classes.wrapper} data-testid="pacing-bar-outline">
            {children}
            <div
                aria-hidden
                className={clsx(classes.track, isVisible && classes.trackVisible)}
                data-testid="pacing-progress-bar"
                data-pacing-state={pacing.state}
            >
                <div className={clsx(classes.fill, classes[pacing.state])} style={{ width: `${fillWidth}%` }} />
            </div>
        </div>
    );
}
